import React, { useEffect, useState, useContext } from "react";
import { FormControl, Alert, Button } from "react-bootstrap";
import apiService from "../services/api.service";
import { Link, useHistory } from "react-router-dom";
import FeaturedEventContext from "../context/FeaturedEventContext";
import FlashAlert from "../Components/FlashAlert/FlashAlert";
import "./userList.css";
import "./events.css";

const formatDate = (value) => {
  try {
    const date = new Date(value);
    return date.toLocaleDateString("it-IT");
  } catch {
    return value || "";
  }
};

const formatTime = (value) => {
  try {
    const date = new Date(value);
    return date.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
};

const EventsList = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterText, setFilterText] = useState("");
  const [feedback, setFeedback] = useState(null);
  const [downloadBusy, setDownloadBusy] = useState("");
  const history = useHistory();
  const { setFeaturedEventId, setFeaturedEventDate, setFeaturedEventTime } = useContext(FeaturedEventContext);

  useEffect(() => {
    getEventList();
  }, []);

  const getEventList = async () => {
    setLoading(true);
    try {
      const eventListResponse = await apiService.getEvents();
      if (eventListResponse.data && Array.isArray(eventListResponse.data)) {
        const eventsWithData = await Promise.all(
          eventListResponse.data.map(async (event) => {
            try {
              const statusResponse = await apiService.getEventStatus(event.id);
              event.status = statusResponse.data?.status || statusResponse.data || "inactive";
            } catch {
              event.status = "inactive";
            }
            try {
              const attendanceResponse = await apiService.getEventAttendance(event.id);
              event.attendanceCount = attendanceResponse.data?.length || 0;
            } catch {
              event.attendanceCount = 0;
            }
            return event;
          })
        );
        setEvents(eventsWithData);
      } else {
        setEvents([]);
      }
    } catch (error) {
      console.error(error);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredEvents = events.filter((event) => {
    const filter = filterText.toLowerCase();
    if (!filter) return true;
    return (
      (event.name || "").toLowerCase().includes(filter) ||
      (event.description || "").toLowerCase().includes(filter) ||
      formatDate(event.date).toLowerCase().includes(filter) ||
      (event.embed_link || "").toLowerCase().includes(filter) ||
      (event.finished ? "si" : "no").includes(filter) ||
      (event.status === "active" ? "attivo" : "inattivo").includes(filter)
    );
  });

  const handleSetFeaturedEvent = (event) => {
    try {
      setFeaturedEventId(event.id);
      setFeaturedEventDate(formatDate(event.date));
      setFeaturedEventTime(formatTime(event.date));
      setFeedback({ message: "Evento impostato in primo piano", variant: "success" });
    } catch {
      setFeedback({ message: "Errore durante l'impostazione dell'evento", variant: "danger" });
    }
  };

  const downloadAttendance = async (event, format) => {
    const busyKey = `${event.id}-${format}`;
    setDownloadBusy(busyKey);
    try {
      await apiService.downloadEventAttendance(event.id, format);
    } catch {
      setFeedback({ message: `Download ${format.toUpperCase()} non riuscito`, variant: "danger" });
    } finally {
      setDownloadBusy("");
    }
  };

  return (
    <div className="users-page">
      <div className="users-card events-card">
        <div className="users-header">
          <h1 className="users-title">Lista Eventi</h1>
          <div className="events-header-actions">
            <Button className="users-edit-btn" onClick={() => history.push("/events/new")}>
              Nuovo evento
            </Button>
            <Button variant="outline-secondary" onClick={getEventList}>
              Aggiorna
            </Button>
          </div>
        </div>

        {feedback && (
          <FlashAlert variant={feedback.variant} onHidden={() => setFeedback(null)}>
            {feedback.message}
          </FlashAlert>
        )}

        <FormControl
          className="events-filter mb-3"
          type="text"
          placeholder="Filtra eventi"
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
        />

        {loading && <p className="users-loading">Caricamento eventi...</p>}

        {!loading && filteredEvents.length === 0 && (
          <Alert variant="info">Non ci sono eventi registrati</Alert>
        )}

        {!loading && filteredEvents.length > 0 && (
          <div className="users-table events-table">
            {filteredEvents.map((event) => (
              <div className="users-table-row events-row" key={event.id}>
                <div className="users-field">
                  <span className="users-field-label">Nome</span>
                  <p className="users-field-value">{event.name}</p>
                </div>
                <div className="users-field">
                  <span className="users-field-label">Data</span>
                  <p className="users-field-value">
                    {formatDate(event.date)} {formatTime(event.date)}
                  </p>
                </div>
                <div className="users-field">
                  <span className="users-field-label">Stato</span>
                  <p className="users-field-value">
                    {event.status === "active" ? "Attivo" : "Inattivo"}
                    {event.finished ? " · Terminato" : ""}
                  </p>
                </div>
                <div className="users-field">
                  <span className="users-field-label">Partecipanti</span>
                  <p className="users-field-value">{event.attendanceCount}</p>
                </div>
                <div className="users-actions events-actions">
                  <Button as={Link} to={`/events/${event.id}/edit`} className="users-edit-btn">
                    Modifica
                  </Button>
                  <Button as={Link} to={`/events/${event.id}/view`} variant="outline-primary">
                    Presenze
                  </Button>
                  <Button
                    variant="outline-secondary"
                    disabled={Boolean(downloadBusy)}
                    onClick={() => downloadAttendance(event, "pdf")}
                  >
                    {downloadBusy === `${event.id}-pdf` ? "Download..." : "PDF"}
                  </Button>
                  <Button
                    variant="outline-secondary"
                    disabled={Boolean(downloadBusy)}
                    onClick={() => downloadAttendance(event, "xls")}
                  >
                    {downloadBusy === `${event.id}-xls` ? "Download..." : "XLS"}
                  </Button>
                  <Button variant="warning" onClick={() => handleSetFeaturedEvent(event)}>
                    In primo piano
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default EventsList;
